import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePondNames } from '@/features/shared/hooks/usePondNames';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/Card';
import {
  ArrowLeft,
  Wrench,
  Droplets,
  Settings,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Bird,
  Fish,
  Leaf,
  Activity,
  FileText,
  Image as ImageIcon,
  Pencil,
} from 'lucide-react';
import { maintenanceApi } from '@/api/maintenanceApi';
import { LocationDisplay } from '@/shared/components/ui/LocationDisplay';
import type { MaintenanceCheck } from '@/api/maintenanceApi';
import { imageApi } from '@/shared/api/imageApi';
import { ImageGallery } from '@/shared/components/ui/ImageGallery';
import { useImageStore } from '@/shared/stores/imageStore';
import { queryKeys } from '@/shared/api/queryClient';
import { buildRoute } from '@/shared/lib/constants';
import { MetricBox, StatusBadge } from '@/features/shared/components/records';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { format } from 'date-fns';

export const MaintenanceDetailPage: React.FC = () => {
  const { farmId, checkId } = useParams<{
    farmId: string;
    checkId: string;
  }>();
  const pondNames = usePondNames(farmId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pondIdFromUrl = searchParams.get('pondId');
  const { showAxiosError } = useErrorToast();
  const [check, setCheck] = useState<MaintenanceCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // Zustand image store
  const setImages = useImageStore((state) => state.setImages);

  useEffect(() => {
    if (!farmId || !checkId) return;

    const loadCheck = async () => {
      try {
        setLoading(true);
        // Fetch from the list API and find the specific check
        const listResponse = await maintenanceApi.listChecks(farmId, { limit: 100 });
        const checkData = listResponse.data.find((c) => c.id === checkId);
        if (!checkData) throw new Error('Check not found');
        setCheck(checkData);
      } catch (error) {
        showAxiosError(error);
      } finally {
        setLoading(false);
      }
    };

    loadCheck();
  }, [farmId, checkId, showAxiosError]);

  // Fetch signed URLs for images
  const { data: signedUrlsResponse, isLoading: isImagesLoading } = useQuery({
    queryKey: queryKeys.images.signedUrls(check?.imageIds || []),
    queryFn: () => imageApi.getSignedUrls(check?.imageIds || []),
    enabled: !!check?.imageIds && check.imageIds.length > 0,
  });

  // Update Zustand cache when signed URLs are fetched
  useEffect(() => {
    if (signedUrlsResponse?.images) {
      setImages(signedUrlsResponse.images);
    }
  }, [signedUrlsResponse, setImages]);

  const handleImageClick = (index: number) => {
    setGalleryStartIndex(index);
    setShowGallery(true);
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!check) {
    return <div className="p-8 text-center">Maintenance check not found</div>;
  }

  const hasEquipmentIssue = !check.paddleWheelFunctioning || !check.waterFlowCheck;
  const hasEnvironmentalIssue =
    check.algalBloom || check.presenceOfBirds || check.stressedFishAtInlets;
  const hasAnyIssue = hasEquipmentIssue || hasEnvironmentalIssue;
  const hasImages = check.imageIds && check.imageIds.length > 0;
  const images = signedUrlsResponse?.images || [];

  return (
    <>
      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (farmId) {
                navigate(buildRoute.maintenance(farmId, pondIdFromUrl));
              } else {
                navigate(-1);
              }
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/50">
              <Wrench className="h-6 w-6 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {pondNames[check.pondId] || check.pond?.name || `Pond ${check.pondId.slice(0, 8)}`}
              </h1>
              <p className="text-sm text-slate-500">
                {format(new Date(check.checkedAt), 'PPpp')}
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(buildRoute.maintenanceEdit(farmId!, checkId!, pondIdFromUrl))}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <StatusBadge variant={hasAnyIssue ? 'warning' : 'success'}>
              {hasAnyIssue ? 'Issues Found' : 'All Good'}
            </StatusBadge>
          </div>
        </div>

        {/* Status Banner */}
        <Card
          className={`border-l-4 ${
            hasAnyIssue
              ? 'border-l-amber-500 bg-amber-50/50'
              : 'border-l-emerald-500 bg-emerald-50/50'
          }`}
        >
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {hasAnyIssue ? (
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                )}
                <div>
                  <p className="font-semibold text-slate-900">
                    {hasAnyIssue ? 'Attention Required' : 'Systems Operational'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {hasEquipmentIssue && hasEnvironmentalIssue
                      ? 'Equipment and environmental issues detected'
                      : hasEquipmentIssue
                      ? 'Equipment issues detected'
                      : hasEnvironmentalIssue
                      ? 'Environmental issues detected'
                      : 'All equipment functioning normally'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricBox
            label="Water Level"
            value={`${(check.waterLevel ?? 0).toFixed(2)}m`}
            icon={Droplets}
            color="blue"
          />
          <MetricBox
            label="Discharge pH"
            value={check.dischargePh?.toFixed(1) ?? 'N/A'}
            icon={Activity}
            color={
              check.dischargePh && check.dischargePh >= 6.5 && check.dischargePh <= 8.5
                ? 'emerald'
                : 'amber'
            }
          />
          <MetricBox
            label="Discharge DO"
            value={check.dischargeDo ? `${check.dischargeDo.toFixed(1)} mg/L` : 'N/A'}
            icon={Activity}
            color={check.dischargeDo && check.dischargeDo >= 5 ? 'emerald' : 'amber'}
          />
          <MetricBox
            label="Flow Rate"
            value={
              check.dischargeFlowRate
                ? `${check.dischargeFlowRate.toFixed(1)} L/min`
                : 'N/A'
            }
            icon={Activity}
            color="blue"
          />
        </div>

        {/* Equipment Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" />
              Equipment Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Paddle Wheel */}
              <div
                className={`p-4 rounded-lg border ${
                  check.paddleWheelFunctioning
                    ? 'bg-emerald-50/50 border-emerald-200/50'
                    : 'bg-red-50/50 border-red-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {check.paddleWheelFunctioning ? (
                    <CheckCircle className="h-6 w-6 text-emerald-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">Paddle Wheel</p>
                    <p
                      className={`text-sm ${
                        check.paddleWheelFunctioning
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {check.paddleWheelFunctioning ? 'Functioning' : 'Not Functioning'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Water Flow */}
              <div
                className={`p-4 rounded-lg border ${
                  check.waterFlowCheck
                    ? 'bg-emerald-50/50 border-emerald-200/50'
                    : 'bg-red-50/50 border-red-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {check.waterFlowCheck ? (
                    <CheckCircle className="h-6 w-6 text-emerald-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">Water Flow</p>
                    <p
                      className={`text-sm ${
                        check.waterFlowCheck ? 'text-emerald-700' : 'text-red-700'
                      }`}
                    >
                      {check.waterFlowCheck ? 'Normal' : 'Issue Detected'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Environmental Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Leaf className="h-5 w-5 text-slate-600" />
              Environmental Conditions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Algal Bloom */}
              <div
                className={`p-4 rounded-lg border ${
                  check.algalBloom
                    ? 'bg-amber-50/50 border-amber-200/50'
                    : 'bg-slate-50 border-slate-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Leaf
                    className={`h-6 w-6 ${
                      check.algalBloom ? 'text-amber-600' : 'text-slate-400'
                    }`}
                  />
                  <div>
                    <p className="font-semibold text-slate-900">Algal Bloom</p>
                    <p
                      className={`text-sm ${
                        check.algalBloom ? 'text-amber-700' : 'text-slate-500'
                      }`}
                    >
                      {check.algalBloom ? 'Detected' : 'Not Detected'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bird Presence */}
              <div
                className={`p-4 rounded-lg border ${
                  check.presenceOfBirds
                    ? 'bg-amber-50/50 border-amber-200/50'
                    : 'bg-slate-50 border-slate-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Bird
                    className={`h-6 w-6 ${
                      check.presenceOfBirds ? 'text-amber-600' : 'text-slate-400'
                    }`}
                  />
                  <div>
                    <p className="font-semibold text-slate-900">Bird Presence</p>
                    <p
                      className={`text-sm ${
                        check.presenceOfBirds ? 'text-amber-700' : 'text-slate-500'
                      }`}
                    >
                      {check.presenceOfBirds ? 'Birds Present' : 'No Birds'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stressed Fish */}
              <div
                className={`p-4 rounded-lg border ${
                  check.stressedFishAtInlets
                    ? 'bg-red-50/50 border-red-200/50'
                    : 'bg-slate-50 border-slate-200/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Fish
                    className={`h-6 w-6 ${
                      check.stressedFishAtInlets ? 'text-red-600' : 'text-slate-400'
                    }`}
                  />
                  <div>
                    <p className="font-semibold text-slate-900">Stressed Fish</p>
                    <p
                      className={`text-sm ${
                        check.stressedFishAtInlets ? 'text-red-700' : 'text-slate-500'
                      }`}
                    >
                      {check.stressedFishAtInlets ? 'At Inlets' : 'None Observed'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Procedure & Notes */}
        {(check.procedureFollowed || check.notes) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Procedure & Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {check.procedureFollowed && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-600 mb-2">
                    Procedure Followed
                  </p>
                  <p className="text-slate-700">{check.procedureFollowed}</p>
                </div>
              )}
              {check.notes && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-600 mb-2">Notes</p>
                  <p className="text-slate-700">{check.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        {hasImages && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-slate-600" />
                  Photos ({check.imageIds?.length})
                </CardTitle>
                {isImagesLoading && (
                  <span className="text-sm text-slate-500">Loading...</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((image, index) => (
                    <button
                      key={image.imageId}
                      onClick={() => handleImageClick(index)}
                      className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-emerald-500 transition-all"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.fileName}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {isImagesLoading ? 'Loading images...' : 'No images available'}
                  </p>
                </div>
              )}

              {signedUrlsResponse?.unauthorized &&
                signedUrlsResponse.unauthorized.length > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      {signedUrlsResponse.unauthorized.length} image(s) could not be
                      loaded due to permissions
                    </p>
                  </div>
                )}

              {check.hasPendingImages && (
                <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Some images are still pending upload
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardContent className="px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
              <div>
                <span className="text-slate-500">Performed By</span>
                <div className="text-slate-700 mt-0.5">
                  {check.performedByName || <span className="text-slate-400 italic">Unknown</span>}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Checked</span>
                <div className="text-slate-700 mt-0.5">
                  {format(new Date(check.checkedAt), 'PPpp')}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Created</span>
                <div className="text-slate-700 mt-0.5">
                  {format(new Date(check.createdAt), 'PPpp')}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Check ID</span>
                <div className="font-mono text-slate-700 mt-0.5 text-xs break-all">{check.id}</div>
              </div>
              <div>
                <span className="text-slate-500">Location</span>
                <div className="mt-0.5">
                  <LocationDisplay latitude={check.latitude} longitude={check.longitude} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Gallery Lightbox */}
      {showGallery && images.length > 0 && (
        <ImageGallery
          images={images.map((img) => ({
            imageId: img.imageId,
            fileName: img.fileName,
            signedUrl: img.signedUrl,
          }))}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  );
};
